var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { paginatedQuery } from 'lib-shared-api-utils';
import { api } from 'utils/apis/api';
import { createReadSearchResultsSearchParams, transformReadAdvancedSearchResultsArg, } from './utils/transformArg';
import { transformCreateSavedSearchResponse, transformReadAdvancedSearchResultsResponse, transformReadSavedSearchListResponse, transformReadSavedSearchResponse, transformReadSearchEnrichedFiltersResponse, transformReadSearchResultsResponse, transformUpdateSavedSearchResponse, } from './utils/transformResponse';
export const searchApi = api.injectEndpoints({
    endpoints: (builder) => ({
        readSearchConversation: builder.query(getReadSearchEndpointDefinition('conversation')),
        readSearchNote: builder.query(getReadSearchEndpointDefinition('conversation_wrapup')),
        readSearchMessage: builder.query(getReadSearchEndpointDefinition('message')),
        readSearchQueue: builder.query(getReadSearchEndpointDefinition('queue')),
        readSearchTag: builder.query(getReadSearchEndpointDefinition('tag')),
        readSearchCompany: builder.query(getReadSearchEndpointDefinition('company')),
        readSearchUser: builder.query(getReadSearchEndpointDefinition('user')),
        readAdvancedSearchResults: builder.query({
            query: (arg) => {
                const { type, params: body } = transformReadAdvancedSearchResultsArg(arg);
                return {
                    version: 3,
                    path: `search/advanced/${type}`,
                    method: 'POST',
                    body,
                };
            },
            transformResponse: transformReadAdvancedSearchResultsResponse,
        }),
        readAdvancedSearchConversationsIdsBulk: builder.query({
            query: (arg) => {
                const { params: body } = transformReadAdvancedSearchResultsArg(arg);
                return {
                    version: 3,
                    path: 'search/advanced/bulk/ids',
                    method: 'POST',
                    body,
                };
            },
        }),
        readSavedSearchList: builder.query({
            query: () => ({
                version: 3,
                path: 'search/saved',
            }),
            transformResponse: transformReadSavedSearchListResponse,
        }),
        readSavedSearch: builder.query({
            query: ({ id }) => ({
                version: 3,
                path: `search/saved/${id}`,
            }),
            transformResponse: transformReadSavedSearchResponse,
        }),
        createSavedSearch: builder.mutation({
            query: (body) => ({
                version: 3,
                path: 'search/saved',
                method: 'POST',
                body,
            }),
            onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    try {
                        const { data: { data: { saved_search }, }, } = yield queryFulfilled;
                        dispatch(searchApi.util.updateQueryData('readSavedSearchList', undefined, (draft) => {
                            draft.data.saved_searches.push(saved_search);
                        }));
                    }
                    catch (_a) { }
                });
            },
            transformResponse: transformCreateSavedSearchResponse,
        }),
        updateSavedSearch: builder.mutation({
            query: ({ saved_search }) => ({
                version: 3,
                path: `search/saved/${saved_search.id}`,
                method: 'PATCH',
                body: saved_search,
            }),
            onQueryStarted({ saved_search }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchReadSavedSearchListQuery = dispatch(searchApi.util.updateQueryData('readSavedSearchList', undefined, (draft) => {
                        const savedSearchList = draft.data.saved_searches;
                        const index = draft.data.saved_searches.findIndex((savedSearch) => savedSearch.id === saved_search.id);
                        if (index !== -1) {
                            savedSearchList[index] = Object.assign(Object.assign({}, savedSearchList[index]), saved_search);
                        }
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchReadSavedSearchListQuery.undo();
                    }
                });
            },
            transformResponse: transformUpdateSavedSearchResponse,
        }),
        deleteSavedSearch: builder.mutation({
            query: ({ id }) => ({
                version: 3,
                path: `search/saved/${id}`,
                method: 'DELETE',
            }),
            onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchReadSavedSearchListQuery = dispatch(searchApi.util.updateQueryData('readSavedSearchList', undefined, (draft) => {
                        const savedSearchList = draft.data.saved_searches;
                        const index = draft.data.saved_searches.findIndex((savedSearch) => savedSearch.id === id);
                        if (index !== -1) {
                            savedSearchList.splice(index, 1);
                        }
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchReadSavedSearchListQuery.undo();
                    }
                });
            },
        }),
        readSearchEnrichedFilters: builder.query({
            query: ({ filters }) => {
                return {
                    version: 3,
                    path: 'search/saved/validate',
                    method: 'POST',
                    body: {
                        params: {
                            filters,
                        },
                    },
                };
            },
            transformResponse: transformReadSearchEnrichedFiltersResponse,
        }),
    }),
});
/**
 * Gets the endpoint definition for the read search endpoint.
 *
 * @param type The search type.
 */
function getReadSearchEndpointDefinition(type) {
    return {
        queryFn(arg, _api, _extraOptions, baseQuery) {
            return __awaiter(this, void 0, void 0, function* () {
                const response = yield baseQuery(paginatedQuery(arg, (arg) => ({
                    version: 2,
                    path: 'search',
                    params: createReadSearchResultsSearchParams(type, arg),
                }), {
                    sort: {
                        paramName: 'sortBy',
                    },
                    search: {
                        paramName: 'query',
                        default: '',
                        supportsSortedSearch: true,
                    },
                    pagination: {
                        paramNaming: 'pageSizePage',
                        defaultLimit: 50,
                    },
                }));
                return {
                    data: transformReadSearchResultsResponse(response.data, arg),
                };
            });
        },
    };
}
